import { PanoObject } from "@/object-cache";
import { usePanoCameraTransform } from "@/utils/camera-transform";
import {
  CachedWorldTransform,
  Img360PanoBase,
  Img360PanoRef,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { IElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { EventHandlers } from "@react-three/fiber/dist/declarations/src/core/events";
import { forwardRef, useEffect, useRef, type JSX } from "react";
import { type Camera } from "three";

export type PanoramaRendererProps = EventHandlers & {
  /** The pano to render */
  pano: PanoObject;
  /** sheet used to compute pano position if it has inaccurate height */
  sheetForElevation?: IElementGenericImgSheet;
  /** The opacity level to use [0, 1]. A value != 0 will set material.transparent = true */
  opacity?: number;
  /**
   * Define to true to register this as a secondary view for split screen rendering
   *
   * @default false
   */
  isSecondaryView?: boolean;
  /** Callback executed, when the rendered transform of the pano changed */
  onTransformChanged?(transform: CachedWorldTransform): void;
  /** Optional custom camera to render the pano */
  customCamera?: Camera;
};

/**
 * @returns Renderer for panorama images in panorama mode
 */
export const PanoramaRenderer = forwardRef<
  Img360PanoRef,
  PanoramaRendererProps
>(function PanoramaRenderer(
  {
    pano,
    sheetForElevation,
    opacity,
    isSecondaryView = false,
    onTransformChanged,
    customCamera,
    ...rest
  }: PanoramaRendererProps,
  ref,
): JSX.Element | null {
  // The pano should be rendered at "eye-height", so it is centered on the same position as the camera
  const pose = usePanoCameraTransform(pano.iElement, sheetForElevation);

  // When the pose changes for the same pano, 'onTransformChanged' should be called.
  // When the pose changed because the pano changed, 'onTransformChanged' should not be called.
  // Therefore, a ref contains a counter that counts how many times the pose changes while the pano
  // remains the same. After the first time, 'onTransformChanged' is called for every pose change.
  const numPoseChanges = useRef(0);
  useEffect(() => {
    numPoseChanges.current = 0;
  }, [pano.iElement.id]);

  // Callback, when the pano transform changes
  useNonExhaustiveEffect(() => {
    // Skipping first pose load for pano
    if (numPoseChanges.current > 0) {
      onTransformChanged?.(pose);
    }
    numPoseChanges.current += 1;
  }, [pose]);

  if (opacity === 0) return null;

  return (
    <Img360PanoBase
      pano={pano}
      ref={ref}
      opacity={opacity}
      isSecondaryView={isSecondaryView}
      position={pose.position}
      quaternion={pose.quaternion}
      customCamera={customCamera}
      {...rest}
    />
  );
});
